import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';


const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess(false);
    setEmailError(null);

    // Validation
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Sign up with email confirmation disabled (we handle it manually)
      const { data, error: signUpError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.fullName,
            role: 'user'
          },
          emailRedirectTo: `${window?.location?.origin}/activate`,
          captchaToken: null // Bypass CAPTCHA verification
        }
      });

      if (signUpError) {
        // Handle specific error cases
        if (signUpError?.message?.includes('User already registered')) {
          setError('This email is already registered. Please sign in instead.');
        } else if (signUpError?.message?.includes('Email rate limit exceeded')) {
          setError('Too many signup attempts. Please try again in a few minutes.');
        } else {
          setError(signUpError?.message);
        }
        return;
      }

      if (data?.user) {
        // Check if user was actually created (not just pending confirmation)
        if (data?.user?.identities?.length === 0) {
          setError('This email is already registered. Please sign in instead.');
          return;
        }

        // Wait for trigger to create profile (increased timeout)
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Get user profile with activation token
        const { data: profileData, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('trial_end_date, activation_token, email_confirmed')
          ?.eq('id', data?.user?.id)
          ?.single();

        if (profileError) {
          console.error('Error fetching profile:', profileError);
          // Try one more time after another delay
          await new Promise(resolve => setTimeout(resolve, 2000));
          const { data: retryProfileData, error: retryProfileError } = await supabase
            ?.from('user_profiles')
            ?.select('trial_end_date, activation_token, email_confirmed')
            ?.eq('id', data?.user?.id)
            ?.single();
          
          if (retryProfileError) {
            console.error('Retry error fetching profile:', retryProfileError);
            setError('Account created but profile setup failed. Please contact support with your email.');
            return;
          }
          
          // Use retry data
          const trialEndDate = retryProfileData?.trial_end_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString();
          const activationToken = retryProfileData?.activation_token;

          if (!activationToken) {
            console.error('No activation token generated after retry');
            setError('Account created but activation token missing. Please contact support.');
            return;
          }

          // Send activation email
          await sendActivationEmail(formData?.email, formData?.fullName, trialEndDate, activationToken, data?.user?.id);
          return;
        }

        const trialEndDate = profileData?.trial_end_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString();
        const activationToken = profileData?.activation_token;

        if (!activationToken) {
          console.error('No activation token generated');
          setError('Account created but activation token missing. Please contact support.');
          return;
        }

        // Send activation email
        await sendActivationEmail(formData?.email, formData?.fullName, trialEndDate, activationToken, data?.user?.id);
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to send activation email
  const sendActivationEmail = async (email, fullName, trialEndDate, activationToken, userId) => {
    const activationUrl = `${window?.location?.origin}/activate?token=${activationToken}`;

    try {
      const { data: emailData, error: emailError } = await supabase?.functions?.invoke('send-activation', {
        body: {
          email: email,
          fullName: fullName,
          trialEndDate: trialEndDate,
          activationToken: activationToken,
          activationUrl: activationUrl
        }
      });

      if (emailError) {
        console.error('Failed to send activation email:', emailError);
        const errorDetails = emailData?.error || emailError?.message || 'Unknown error';
        const errorHint = emailData?.details || emailData?.hint || '';
        console.error('Email error details:', { errorDetails, errorHint, fullResponse: emailData });
        
        // Set email error but still show success (account was created)
        setEmailError({
          message: errorDetails,
          hint: errorHint,
          userId: userId,
          activationToken: activationToken
        });
        setSuccess(true);
        setEmailSent(false);
        return;
      } else if (emailData?.error) {
        console.error('Email service returned error:', emailData);
        
        // Set email error but still show success (account was created)
        setEmailError({
          message: emailData?.error,
          hint: emailData?.hint || emailData?.details || '',
          userId: userId,
          activationToken: activationToken
        });
        setSuccess(true);
        setEmailSent(false);
        return;
      } else {
        console.log('Activation email sent successfully:', emailData);
        setEmailSent(true);
        setSuccess(true);
        setEmailError(null);
      }
    } catch (emailErr) {
      console.error('Error sending activation email:', emailErr);
      
      // Set email error but still show success (account was created)
      setEmailError({
        message: 'Failed to send activation email',
        hint: 'Please contact support to resend the activation email',
        userId: userId,
        activationToken: activationToken
      });
      setSuccess(true);
      setEmailSent(false);
      return;
    }
  };

  const handleResendEmail = async () => {
    if (!emailError?.userId || !emailError?.activationToken) {
      return;
    }

    setLoading(true);
    setEmailError(null);

    try {
      // Get user profile again
      const { data: profileData, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('trial_end_date, activation_token, email_confirmed')
        ?.eq('id', emailError?.userId)
        ?.single();

      if (profileError || !profileData?.activation_token) {
        setEmailError({
          ...emailError,
          message: 'Unable to resend email. Please contact support.',
          hint: ''
        });
        return;
      }

      await sendActivationEmail(
        formData?.email,
        formData?.fullName,
        profileData?.trial_end_date,
        profileData?.activation_token,
        emailError?.userId
      );
    } catch (err) {
      console.error('Error resending email:', err);
      setEmailError({
        ...emailError,
        message: 'Failed to resend email',
        hint: 'Please try again or contact support'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg">
              <Icon name="Building2" size={24} className="text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">PNG HR Analytics</h1>
          <p className="text-gray-600 dark:text-gray-400">Create your account</p>
        </div>

        {/* Signup Form */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-8">
          {success && emailSent ? (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-3">
                  <Icon name="Mail" size={48} className="text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Check Your Email!
              </h3>
              <div className="space-y-3 text-left bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  We've sent a confirmation link to:
                </p>
                <p className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                  {formData?.email}
                </p>
                <div className="border-t border-blue-200 dark:border-blue-800 pt-3 mt-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Next Steps:
                  </p>
                  <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-2 list-decimal list-inside">
                    <li>Open your email inbox</li>
                    <li>Click the confirmation link</li>
                    <li>You'll be redirected to localhost (this is normal)</li>
                    <li>Come back here and sign in</li>
                  </ol>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-3">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 dark:text-amber-200">
                      <strong>Important:</strong> The localhost redirect after clicking the email link is expected behavior. Your email will be verified successfully.
                    </p>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full mt-4"
              >
                <span className="flex items-center justify-center gap-2">
                  <Icon name="ArrowRight" size={18} />
                  Go to Sign In
                </span>
              </Button>
            </div>
          ) : success && emailError ? (
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center">
                <div className="rounded-full bg-red-100 dark:bg-red-900/30 p-3">
                  <Icon name="AlertCircle" size={48} className="text-red-600 dark:text-red-400" />
                </div>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Error sending confirmation email
              </h3>
              <div className="space-y-3 text-left bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Your account was created successfully, but we couldn't send the confirmation email.
                </p>
                <div className="bg-white dark:bg-slate-700 rounded-lg p-3 border border-red-200 dark:border-red-700">
                  <p className="text-xs font-mono text-red-800 dark:text-red-200">
                    {emailError?.message}
                  </p>
                  {emailError?.hint && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                      {emailError?.hint}
                    </p>
                  )}
                </div>
                <div className="border-t border-red-200 dark:border-red-800 pt-3 mt-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    What to do next:
                  </p>
                  <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 list-disc list-inside">
                    <li>Try resending the confirmation email using the button below</li>
                    <li>Contact support at <a href="mailto:support@hrpacificrbs.com" className="text-blue-600 dark:text-blue-400 hover:underline">support@hrpacificrbs.com</a></li>
                    <li>Include your email address: <strong>{formData?.email}</strong></li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleResendEmail}
                  disabled={loading}
                  className="flex-1"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="Loader2" size={18} className="animate-spin" />
                      Resending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="RefreshCw" size={18} />
                      Resend Email
                    </span>
                  )}
                </Button>
                <Button
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="flex-1"
                >
                  <span className="flex items-center justify-center gap-2">
                    <Icon name="ArrowRight" size={18} />
                    Go to Sign In
                  </span>
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              )}

              {/* Trial Info Banner */}
              <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 flex items-start gap-3">
                <Icon name="Gift" size={20} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">7-Day Free Trial</p>
                  <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                    Full access to all features. No credit card required. Cancel anytime.
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Name
                </label>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  value={formData?.fullName}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="your.email@company.com"
                  required
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData?.password}
                    onChange={handleChange}
                    placeholder="At least 6 characters"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confirm Password
                </label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData?.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  required
                  disabled={loading}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <Icon name="Loader2" size={18} className="animate-spin" />
                    Creating Account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </Button>

              <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                Already have an account?{' '}
                <Link to="/login" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">
                  Sign In
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;